import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, Clock, LogIn, LogOut, Loader2 } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { useAdmin } from "@/contexts/AdminContext";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

function fmtDuration(fromIso, nowMs) {
  if (!fromIso) return "0:00:00";
  const start = new Date(fromIso).getTime();
  let secs = Math.max(0, Math.floor((nowMs - start) / 1000));
  const h = Math.floor(secs / 3600); secs -= h * 3600;
  const m = Math.floor(secs / 60); secs -= m * 60;
  return `${h}:${String(m).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

// v2.8+E343 · dispatchers plus supervisors and above can clock in.
const CLOCK_ROLES = new Set([
  "dispatcher", "supervisor", "field_supervisor", "team_leader", "admin", "general_partner",
  "operating_partner", "developer", "super_user", "platform_owner",
]);

/**
 * Time Clock button (top nav). Opens a modal to clock in / out.
 * Each event is tied to the signed-in account server-side.
 */
export const TimeClockButton = () => {
  const { admin } = useAdmin();
  const canClock = [admin?.role, admin?.canonical_role, ...(admin?.roles || [])]
    .some((r) => CLOCK_ROLES.has(String(r || "").toLowerCase()));

  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [nowMs, setNowMs] = useState(Date.now());
  const tick = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/timeclock/status");
      setStatus(data);
    } catch (_e) {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Lightweight status fetch on mount so the button reflects clocked-in state.
  useEffect(() => {
    if (canClock) load();
  }, [canClock, load]);

  // 1s ticker for the live on-shift timer (only while clocked in).
  useEffect(() => {
    if (status?.clocked_in) {
      tick.current = setInterval(() => setNowMs(Date.now()), 1000);
      return () => clearInterval(tick.current);
    }
    return undefined;
  }, [status?.clocked_in]);

  const doClock = async (action) => {
    setBusy(true);
    try {
      await adminApi.post(`/admin/timeclock/${action}`);
      toast.success(action === "clock-in" ? "Clocked in" : "Clocked out");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update the time clock");
    } finally {
      setBusy(false);
    }
  };

  if (!canClock) return null;

  const clockedIn = !!status?.clocked_in;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => { setOpen(true); load(); }}
        data-testid="timeclock-open-btn"
        className={`h-8 text-xs ${clockedIn
          ? "bg-emerald-500/10 border-emerald-500/50 text-emerald-600 dark:text-emerald-300 hover:bg-emerald-500/20"
          : "bg-white border-slate-300 text-slate-800 hover:bg-slate-100 hover:text-slate-900 dark:bg-zinc-950 dark:border-zinc-700 dark:text-zinc-300 dark:hover:text-amber-400"}`}
      >
        {clockedIn
          ? <span className="h-2 w-2 rounded-full bg-emerald-500 mr-1.5 animate-pulse inline-block"/>
          : <Clock className="h-3 w-3 mr-1"/>}
        {clockedIn ? "Clocked In" : "Time"}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="timeclock-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-500"/>Time Clock
            </DialogTitle>
            <DialogDescription>
              {admin?.name ? `${admin.name} · ` : ""}Clock in when you start your shift, clock out when you finish.
            </DialogDescription>
          </DialogHeader>

          {loading && !status ? (
            <div className="flex items-center justify-center py-10 text-zinc-400">
              <Loader2 className="h-5 w-5 animate-spin"/>
            </div>
          ) : (
            <div className="space-y-4">
              <div
                data-testid="timeclock-state"
                className={`rounded-xl border p-4 text-center ${clockedIn
                  ? "border-emerald-500/40 bg-emerald-500/5"
                  : "border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-900/60"}`}
              >
                {clockedIn ? (
                  <>
                    <div className="text-xs uppercase tracking-widest font-bold text-emerald-600 dark:text-emerald-300">On shift</div>
                    <div className="text-3xl font-black tabular-nums mt-1 text-emerald-600 dark:text-emerald-300" data-testid="timeclock-live-timer">
                      {fmtDuration(status?.open_entry?.clock_in, nowMs)}
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-1">
                      Since {status?.open_entry?.clock_in ? new Date(status.open_entry.clock_in).toLocaleTimeString() : "—"}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-xs uppercase tracking-widest font-bold text-zinc-500">Clocked out</div>
                    <div className="text-2xl font-black mt-1 text-zinc-700 dark:text-zinc-300">Not on shift</div>
                  </>
                )}
              </div>

              {!clockedIn && status?.last_auto_closed && (
                <div data-testid="timeclock-auto-closed-notice" className="flex gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5"/>
                  <span>
                    Your shift from {new Date(status.last_auto_closed.clock_in).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} was
                    automatically clocked out at {new Date(status.last_auto_closed.clock_out).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })} (your scheduled end). A supervisor will review it.
                  </span>
                </div>
              )}

              <div className="flex items-center justify-between rounded-lg border border-zinc-200 dark:border-zinc-800 px-3 py-2 text-sm">
                <span className="text-zinc-500">This week (Mon–Sun)</span>
                <span className="font-bold tabular-nums" data-testid="timeclock-period-hours">
                  {(status?.period_hours ?? 0).toFixed(2)} hrs
                </span>
              </div>

              {clockedIn ? (
                <Button
                  onClick={() => doClock("clock-out")}
                  disabled={busy}
                  data-testid="timeclock-clockout-btn"
                  className="w-full h-11 bg-rose-500 hover:bg-rose-600 text-white font-bold"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2"/> : <LogOut className="h-4 w-4 mr-2"/>}Clock Out
                </Button>
              ) : (
                <Button
                  onClick={() => doClock("clock-in")}
                  disabled={busy}
                  data-testid="timeclock-clockin-btn"
                  className="w-full h-11 bg-emerald-500 hover:bg-emerald-600 text-white font-bold"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2"/> : <LogIn className="h-4 w-4 mr-2"/>}Clock In
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TimeClockButton;
